import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Numuv Pagination — reusable numbered pagination with page-size selector.
 *
 * Props:
 *   page         — current 1-based page number
 *   pageSize     — current page size
 *   total        — total number of items
 *   onPageChange(nextPage)
 *   onPageSizeChange(nextSize)
 *   sizeOptions  — [10, 20, 50, 100] by default
 *   testIdPrefix — data-testid prefix (default 'pagination')
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  sizeOptions = [10, 20, 50, 100],
  testIdPrefix = 'pagination',
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total || 0);

  const pages = buildPageList(safePage, totalPages);

  const goto = (p) => {
    if (p < 1 || p > totalPages || p === safePage) return;
    onPageChange && onPageChange(p);
  };

  const btn = "min-w-[32px] h-8 px-2 inline-flex items-center justify-center rounded-md text-xs border transition-colors";
  const idle = "bg-white dark:bg-[#111114] border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-400 hover:border-slate-300 dark:hover:border-zinc-700 hover:text-slate-900 dark:hover:text-white";
  const active = "bg-slate-900 dark:bg-white border-slate-900 dark:border-white text-white dark:text-black font-medium";
  const disabled = "opacity-40 cursor-not-allowed";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-500">
        <span data-testid={`${testIdPrefix}-summary`}>
          {total === 0 ? 'No results' : `${start}–${end} of ${total}`}
        </span>
        <span className="mx-1 text-slate-300 dark:text-zinc-700">·</span>
        <label className="inline-flex items-center gap-1.5">
          Rows
          <select
            value={pageSize}
            onChange={(e) => {
              const next = Number(e.target.value);
              onPageSizeChange && onPageSizeChange(next);
            }}
            className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-md px-2 py-1 text-xs text-slate-700 dark:text-zinc-300"
            data-testid={`${testIdPrefix}-size`}
          >
            {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-1">
        <button className={`${btn} ${safePage === 1 ? disabled : idle}`} onClick={() => goto(1)} disabled={safePage === 1} data-testid={`${testIdPrefix}-first`} title="First page">
          <ChevronsLeft className="w-3.5 h-3.5" />
        </button>
        <button className={`${btn} ${safePage === 1 ? disabled : idle}`} onClick={() => goto(safePage - 1)} disabled={safePage === 1} data-testid={`${testIdPrefix}-prev`} title="Previous">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {pages.map((p, i) => p === '…' ? (
          <span key={`ellipsis-${i}`} className="px-1 text-slate-400 dark:text-zinc-600 text-xs">…</span>
        ) : (
          <button
            key={p}
            className={`${btn} ${p === safePage ? active : idle}`}
            onClick={() => goto(p)}
            data-testid={`${testIdPrefix}-page-${p}`}
          >
            {p}
          </button>
        ))}
        <button className={`${btn} ${safePage === totalPages ? disabled : idle}`} onClick={() => goto(safePage + 1)} disabled={safePage === totalPages} data-testid={`${testIdPrefix}-next`} title="Next">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button className={`${btn} ${safePage === totalPages ? disabled : idle}`} onClick={() => goto(totalPages)} disabled={safePage === totalPages} data-testid={`${testIdPrefix}-last`} title="Last page">
          <ChevronsRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * usePagination — small hook that slices any array into pages.
 *
 * Usage:
 *   const p = usePagination(items, 20);
 *   {p.pageItems.map(...)}
 *   <Pagination {...p.paginationProps} />
 */
export function usePagination(items, initialSize = 20) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialSize);
  const list = Array.isArray(items) ? items : [];
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = list.slice((safePage - 1) * pageSize, safePage * pageSize);
  // Reset to page 1 when the underlying dataset shrinks or filters change
  React.useEffect(() => { if (page > totalPages) setPage(1); }, [total, pageSize, page, totalPages]);
  return {
    page: safePage,
    pageSize,
    pageItems,
    setPage,
    setPageSize: (s) => { setPageSize(s); setPage(1); },
    paginationProps: {
      page: safePage,
      pageSize,
      total,
      onPageChange: setPage,
      onPageSizeChange: (s) => { setPageSize(s); setPage(1); },
    },
  };
}

function buildPageList(current, total) {
  // Build a compact page list like: [1, '…', 4, 5, 6, '…', 12]
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push('…');
  }
  return out;
}
